import { useState, useEffect, useRef, useCallback } from 'react';
import posthog from 'posthog-js';
import {
  Play, Pause, RotateCcw, Settings, Search, X, ChevronRight,
  Maximize2, Minimize2, Plus, Music, Image, ListTodo,
  CloudRain, Trees, Flame, Waves, Coffee, Wind, Volume2
} from 'lucide-react';
import { saveWork, generateId } from '../storage';
import { awardTaskPoints } from '../points';
import './PomodoroTimer.css';

/* ── SCENES ─────────────────────────────────────────────────── */
const SCENES = [
  // Gradient scenes
  { id: 'aurora',   label: 'Aurora',     type: 'gradient', gradient: 'linear-gradient(-45deg, #0f0c29, #302b63, #24243e, #1a0533, #2d1b69, #0f0c29)' },
  { id: 'sunset',   label: 'Sunset',     type: 'gradient', gradient: 'linear-gradient(-45deg, #ee7752, #e73c7e, #23a6d5, #23d5ab, #f093fb, #ee7752)' },
  { id: 'ocean',    label: 'Ocean',      type: 'gradient', gradient: 'linear-gradient(-45deg, #0c3547, #1565c0, #0d47a1, #004d40, #00695c, #0c3547)' },
  { id: 'forest',   label: 'Forest',     type: 'gradient', gradient: 'linear-gradient(-45deg, #134e5e, #1b5e20, #2e7d32, #004d40, #1a237e, #134e5e)' },
  { id: 'bloom',    label: 'Bloom',      type: 'gradient', gradient: 'linear-gradient(-45deg, #f093fb, #f5576c, #fda085, #f093fb, #a18cd1, #fbc2eb)' },
  { id: 'midnight', label: 'Midnight',   type: 'gradient', gradient: 'linear-gradient(-45deg, #0a0a0a, #1a1a2e, #16213e, #0f3460, #1a1a2e, #0a0a0a)' },
  // Animated pixel scenes
  { id: 'pixel-rain',  label: 'Pixel Rain',   type: 'animated', css: 'scene-pixel-rain' },
  { id: 'pixel-stars', label: 'Starfield',    type: 'animated', css: 'scene-pixel-stars' },
  { id: 'pixel-city',  label: 'Night City',   type: 'animated', css: 'scene-pixel-city' },
  { id: 'pixel-fire',  label: 'Campfire',     type: 'animated', css: 'scene-pixel-fire' },
  { id: 'pixel-snow',  label: 'Snowfall',     type: 'animated', css: 'scene-pixel-snow' },
  { id: 'pixel-neon',  label: 'Neon Grid',    type: 'animated', css: 'scene-pixel-neon' },
];

/* ── MOTIVATIONAL QUOTES ────────────────────────────────────── */
const QUOTES = [
  { text: "The will to win, begins within", author: null },
  { text: "Focus on being productive, not busy", author: null },
  { text: "Small steps every day lead to big results", author: null },
  { text: "Your future is created by what you do today", author: null },
  { text: "Stay focused, go after your dreams", author: null },
  { text: "The secret of getting ahead is getting started", author: "Mark Twain" },
  { text: "Don't watch the clock; do what it does. Keep going", author: "Sam Levenson" },
  { text: "It's not about having time, it's about making time", author: null },
  { text: "Discipline is choosing what you want most over what you want now", author: null },
  { text: "Deep work is the superpower of the 21st century", author: "Cal Newport" },
  { text: "Where focus goes, energy flows", author: "Tony Robbins" },
  { text: "One thing at a time. Most important thing first", author: null },
];

/* ── AMBIENT SOUNDS ─────────────────────────────────────────── */
const AMBIENT_SOUNDS = [
  { id: 'rain',      label: 'Rain',      icon: CloudRain, url: 'https://cdn.pixabay.com/audio/2022/10/30/audio_711e7cd849.mp3' },
  { id: 'forest',    label: 'Forest',    icon: Trees,     url: 'https://cdn.pixabay.com/audio/2022/08/31/audio_419263a638.mp3' },
  { id: 'campfire',  label: 'Fire',      icon: Flame,     url: 'https://cdn.pixabay.com/audio/2024/11/04/audio_78e3881233.mp3' },
  { id: 'waves',     label: 'Waves',     icon: Waves,     url: 'https://cdn.pixabay.com/audio/2022/05/31/audio_980f3668f0.mp3' },
  { id: 'cafe',      label: 'Café',      icon: Coffee,    url: 'https://cdn.pixabay.com/audio/2024/09/27/audio_a4884a7ef8.mp3' },
  { id: 'wind',      label: 'Wind',      icon: Wind,      url: 'https://cdn.pixabay.com/audio/2022/03/09/audio_c5209d9192.mp3' },
];

const SPOTIFY_KEY = 'pomo_spotify_url';
const SCENE_KEY = 'pomo_scene';

function getSpotifyEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (!u.hostname.includes('spotify.com')) return null;
    const path = u.pathname;
    return `https://open.spotify.com/embed${path}?utm_source=generator&theme=0`;
  } catch { return null; }
}

export default function PomodoroTimer({ works, onWorkUpdate, uid }) {
  const [timeLeft, setTimeLeft]         = useState(25 * 60);
  const [isRunning, setIsRunning]       = useState(false);
  const [mode, setMode]                 = useState('work');
  const [workDuration, setWorkDuration] = useState(25);
  const [breakDuration, setBreakDuration] = useState(5);
  const [longBreakDuration, setLongBreakDuration] = useState(15);
  const [showSettings, setShowSettings] = useState(false);
  const [showTasks, setShowTasks]       = useState(false);
  const [showMusic, setShowMusic]       = useState(false);
  const [showScenes, setShowScenes]     = useState(false);
  const [searchQuery, setSearchQuery]   = useState('');
  const [selectedWorkId, setSelectedWorkId] = useState(null);
  const [toast, setToast]               = useState({ visible: false, pts: 0 });
  const [pomodoroFocus, setPomodoroFocus] = useState(false);
  const [todoInput, setTodoInput]       = useState('');
  const [scene, setScene]               = useState(() => localStorage.getItem(SCENE_KEY) || 'aurora');
  const [quote] = useState(() => QUOTES[Math.floor(Math.random() * QUOTES.length)]);
  const [spotifyUrl, setSpotifyUrl]     = useState(() => localStorage.getItem(SPOTIFY_KEY) || '');
  const [spotifyInput, setSpotifyInput] = useState('');
  const [activeSounds, setActiveSounds] = useState({});
  const [soundVolumes, setSoundVolumes] = useState({});

  const timerRef   = useRef(null);
  const pageRef    = useRef(null);
  const toastTimer = useRef(null);
  const audioRefs  = useRef({});

  const getTotalSeconds = useCallback((m) => {
    if (m === 'work') return workDuration * 60;
    if (m === 'longBreak') return longBreakDuration * 60;
    return breakDuration * 60;
  }, [workDuration, breakDuration, longBreakDuration]);

  const totalSeconds = getTotalSeconds(mode);

  useEffect(() => {
    if (!isRunning) setTimeLeft(getTotalSeconds(mode));
  }, [workDuration, breakDuration, longBreakDuration, mode]); // eslint-disable-line

  useEffect(() => {
    if (!selectedWorkId) return;
    if (works.some(work => work.id === selectedWorkId)) return;
    setSelectedWorkId(null);
    setTodoInput('');
  }, [selectedWorkId, works]);

  useEffect(() => { setTodoInput(''); }, [selectedWorkId]);

  useEffect(() => {
    function handleFullscreenChange() {
      if (!document.fullscreenElement) setPomodoroFocus(false);
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // Cleanup audio only on component unmount
  useEffect(() => {
    const refs = audioRefs;
    return () => {
      Object.values(refs.current).forEach(a => { try { a.pause(); a.src = ''; } catch {} });
    };
  }, []);

  const handleComplete = useCallback(() => {
    setIsRunning(false);
    clearInterval(timerRef.current);
    posthog.capture('pomodoro_session_completed', { mode });
    setMode(m => (m === 'work' ? 'break' : 'work'));
  }, [mode]);

  useEffect(() => {
    if (isRunning) {
      timerRef.current = setInterval(() => {
        setTimeLeft(p => { if (p <= 1) { handleComplete(); return 0; } return p - 1; });
      }, 1000);
    } else clearInterval(timerRef.current);
    return () => clearInterval(timerRef.current);
  }, [isRunning, handleComplete]);

  const formatTime = s => {
    const m  = Math.floor(s / 60).toString().padStart(2, '0');
    const sc = (s % 60).toString().padStart(2, '0');
    return `${m}:${sc}`;
  };

  function showToastMsg(pts) {
    setToast({ visible: true, pts });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(t => ({ ...t, visible: false })), 1800);
  }

  async function persist(w) { onWorkUpdate(w); if (uid) await saveWork(uid, w); }

  async function toggleTodo(id) {
    if (!selectedWork) return;
    const todo = activeTodos.find(t => t.id === id);
    if (!todo || todo.done) return;
    const newTodos   = activeTodos.filter(t => t.id !== id);
    const newHistory = [{ ...todo, done: true, completedAt: Date.now() }, ...(selectedWork.history || [])];
    await persist({ ...selectedWork, todos: newTodos, history: newHistory });
    if (uid) { await awardTaskPoints(uid); showToastMsg(10); }
  }

  const filteredWorks = works.filter(w =>
    w.title.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const selectedWork = works.find(w => w.id === selectedWorkId);
  const activeTodos  = selectedWork?.todos || [];

  function addTodo() {
    if (!todoInput.trim() || !selectedWork) return;
    const todo = { id: generateId(), text: todoInput.trim(), done: false, createdAt: Date.now() };
    persist({ ...selectedWork, todos: [...activeTodos, todo] });
    setTodoInput('');
  }

  async function toggleFocusMode() {
    try {
      if (!pomodoroFocus) {
        if (window.innerWidth > 768 && pageRef.current) {
          await pageRef.current.requestFullscreen();
        }
        setPomodoroFocus(true);
      } else {
        if (document.fullscreenElement) await document.exitFullscreen();
        setPomodoroFocus(false);
      }
    } catch { setPomodoroFocus(false); }
  }

  function changeScene(id) {
    setScene(id);
    localStorage.setItem(SCENE_KEY, id);
    setShowScenes(false);
  }

  function toggleSound(soundId) {
    const isCurrentlyActive = !!activeSounds[soundId];
    if (isCurrentlyActive) {
      // Stop
      if (audioRefs.current[soundId]) {
        audioRefs.current[soundId].pause();
        audioRefs.current[soundId].currentTime = 0;
      }
      setActiveSounds(prev => { const n = { ...prev }; delete n[soundId]; return n; });
    } else {
      // Play
      const sound = AMBIENT_SOUNDS.find(s => s.id === soundId);
      if (sound) {
        if (!audioRefs.current[soundId]) {
          const audio = new Audio(sound.url);
          audio.loop = true;
          audio.crossOrigin = 'anonymous';
          audio.preload = 'auto';
          audioRefs.current[soundId] = audio;
        }
        const audio = audioRefs.current[soundId];
        audio.volume = soundVolumes[soundId] ?? 0.5;
        audio.play().catch(() => {});
        setActiveSounds(prev => ({ ...prev, [soundId]: true }));
      }
    }
  }

  function changeSoundVolume(soundId, vol) {
    setSoundVolumes(prev => ({ ...prev, [soundId]: vol }));
    if (audioRefs.current[soundId]) {
      audioRefs.current[soundId].volume = vol;
    }
  }

  function stopAllSounds() {
    Object.keys(activeSounds).forEach(id => {
      if (audioRefs.current[id]) {
        audioRefs.current[id].pause();
        audioRefs.current[id].currentTime = 0;
      }
    });
    setActiveSounds({});
  }

  function saveSpotify() {
    setSpotifyUrl(spotifyInput);
    localStorage.setItem(SPOTIFY_KEY, spotifyInput);
  }

  function clearSpotify() {
    setSpotifyUrl('');
    setSpotifyInput('');
    localStorage.removeItem(SPOTIFY_KEY);
  }

  function switchMode(m) {
    setMode(m);
    setIsRunning(false);
  }

  function closeAllPanels() {
    setShowSettings(false);
    setShowTasks(false);
    setShowMusic(false);
    setShowScenes(false);
  }

  function togglePanel(panel) {
    const isOpen = panel === 'tasks' ? showTasks : panel === 'music' ? showMusic : panel === 'scenes' ? showScenes : showSettings;
    closeAllPanels();
    if (!isOpen) {
      if (panel === 'tasks') setShowTasks(true);
      else if (panel === 'music') setShowMusic(true);
      else if (panel === 'scenes') setShowScenes(true);
      else if (panel === 'settings') setShowSettings(true);
    }
  }

  const currentScene = SCENES.find(s => s.id === scene) || SCENES[0];
  const spotifyEmbed = getSpotifyEmbedUrl(spotifyUrl);
  const anySoundPlaying = Object.keys(activeSounds).length > 0;

  // Build scene class
  const sceneClass = currentScene.type === 'animated' ? currentScene.css : '';

  return (
    <div
      ref={pageRef}
      className={`flocus-page ${pomodoroFocus ? 'flocus-fullscreen' : ''} ${sceneClass}`}
      style={currentScene.type === 'gradient' ? { '--scene-gradient': currentScene.gradient } : undefined}
    >
      {currentScene.type === 'gradient' && <div className="flocus-bg" />}
      {currentScene.type === 'animated' && <div className="flocus-animated-bg" />}

      {/* ── QUOTE ── */}
      <div className="flocus-quote">
        <span>"{quote.text}"</span>
        {quote.author && <span className="flocus-quote-author">— {quote.author}</span>}
      </div>

      {/* ── MAIN CENTER ── */}
      <div className="flocus-center">
        <p className="flocus-heading">What do you want to focus on?</p>

        <div className="flocus-modes">
          <button className={`flocus-mode-pill ${mode === 'work' ? 'active' : ''}`} onClick={() => switchMode('work')}>Focus</button>
          <button className={`flocus-mode-pill ${mode === 'break' ? 'active' : ''}`} onClick={() => switchMode('break')}>Short Break</button>
          <button className={`flocus-mode-pill ${mode === 'longBreak' ? 'active' : ''}`} onClick={() => switchMode('longBreak')}>Long Break</button>
        </div>

        <div className="flocus-dots">
          {[0, 1, 2, 3].map(i => (
            <span key={i} className={`flocus-dot ${i === 0 ? 'active' : ''}`} />
          ))}
        </div>

        <div className={`flocus-timer ${isRunning ? 'running' : ''}`}>
          {formatTime(timeLeft)}
        </div>

        <div className="flocus-actions">
          <button className="flocus-start-btn" onClick={() => {
            if (!isRunning) posthog.capture('pomodoro_started', { mode });
            setIsRunning(r => !r);
          }}>
            {isRunning ? <Pause size={18} /> : <Play size={18} style={{ marginLeft: 2 }} />}
            {isRunning ? 'Pause' : 'Start'}
          </button>
          <button className="flocus-reset-btn" onClick={() => { setIsRunning(false); setTimeLeft(totalSeconds); }} title="Reset">
            <RotateCcw size={16} />
          </button>
        </div>
      </div>

      {/* ── NOW PLAYING BAR (visible when sounds active & music panel closed) ── */}
      {anySoundPlaying && !showMusic && (
        <div className="flocus-now-playing">
          <Volume2 size={14} />
          <span>{Object.keys(activeSounds).map(id => AMBIENT_SOUNDS.find(s => s.id === id)?.label).filter(Boolean).join(', ')}</span>
          <button onClick={stopAllSounds} title="Stop all"><X size={12} /></button>
        </div>
      )}

      {/* ── BOTTOM TOOLBAR ── */}
      <div className="flocus-toolbar">
        <div className="flocus-toolbar-left">
          <button className={`flocus-tool-btn ${showTasks ? 'active' : ''}`} onClick={() => togglePanel('tasks')} title="Tasks">
            <ListTodo size={18} />
          </button>
          <button className={`flocus-tool-btn ${showMusic ? 'active' : ''} ${anySoundPlaying ? 'playing' : ''}`} onClick={() => togglePanel('music')} title="Music">
            <Music size={18} />
          </button>
          <button className={`flocus-tool-btn ${showScenes ? 'active' : ''}`} onClick={() => togglePanel('scenes')} title="Scenes">
            <Image size={18} />
          </button>
        </div>
        <div className="flocus-toolbar-right">
          <button className={`flocus-tool-btn ${showSettings ? 'active' : ''}`} onClick={() => togglePanel('settings')} title="Settings">
            <Settings size={18} />
          </button>
          <button className="flocus-tool-btn" onClick={toggleFocusMode} title={pomodoroFocus ? 'Exit Fullscreen' : 'Fullscreen'}>
            {pomodoroFocus ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
        </div>
      </div>

      {/* ── TASKS PANEL ── */}
      {showTasks && (
        <div className="flocus-panel flocus-panel-left">
          <div className="flocus-panel-header">
            <span>Tasks</span>
            <button className="flocus-panel-close" onClick={() => setShowTasks(false)}><X size={16} /></button>
          </div>
          <div className="flocus-panel-body">
            {!selectedWork ? (
              <>
                <div className="flocus-search">
                  <Search size={14} />
                  <input type="text" placeholder="Search works..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                </div>
                <div className="flocus-work-list">
                  {filteredWorks.length === 0 ? <div className="flocus-empty">No works found.</div> : filteredWorks.map(w => (
                    <button key={w.id} className="flocus-work-item" onClick={() => setSelectedWorkId(w.id)}>
                      <span>{w.title}</span><ChevronRight size={13} style={{ opacity: 0.4 }} />
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="flocus-selected-header">
                  <span>{selectedWork.title}</span>
                  <button className="flocus-panel-close" onClick={() => setSelectedWorkId(null)}><X size={13} /></button>
                </div>
                <div className="flocus-todo-list">
                  {activeTodos.length === 0 ? <div className="flocus-empty">No tasks yet.</div> : activeTodos.map(todo => (
                    <div key={todo.id} className="flocus-todo-item">
                      <button className="flocus-todo-check" onClick={() => toggleTodo(todo.id)} />
                      <span>{todo.text}</span>
                    </div>
                  ))}
                </div>
                <div className="flocus-todo-add">
                  <input placeholder="Add a task..." value={todoInput} onChange={e => setTodoInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTodo()} maxLength={120} />
                  <button onClick={addTodo} disabled={!todoInput.trim()}><Plus size={16} /></button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── MUSIC PANEL ── */}
      {showMusic && (
        <div className="flocus-panel flocus-panel-left flocus-panel-music">
          <div className="flocus-panel-header">
            <span>Music & Sounds</span>
            <button className="flocus-panel-close" onClick={() => setShowMusic(false)}><X size={16} /></button>
          </div>
          <div className="flocus-panel-body">
            <p className="flocus-section-label">Ambient Sounds</p>
            <div className="flocus-sounds-grid">
              {AMBIENT_SOUNDS.map(s => {
                const Icon = s.icon;
                const isActive = !!activeSounds[s.id];
                return (
                  <div key={s.id} className={`flocus-sound-card ${isActive ? 'active' : ''}`}>
                    <button className="flocus-sound-toggle" onClick={() => toggleSound(s.id)}>
                      <Icon size={20} />
                      <span>{s.label}</span>
                      {isActive && <Volume2 size={12} className="flocus-sound-indicator" />}
                    </button>
                    {isActive && (
                      <input
                        type="range" min="0" max="1" step="0.05"
                        value={soundVolumes[s.id] ?? 0.5}
                        onChange={e => changeSoundVolume(s.id, parseFloat(e.target.value))}
                        className="flocus-sound-volume"
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {anySoundPlaying && (
              <button className="flocus-stop-all" onClick={stopAllSounds}>Stop All Sounds</button>
            )}

            <p className="flocus-section-label" style={{ marginTop: 20 }}>Spotify</p>
            {spotifyEmbed ? (
              <div className="flocus-spotify-embed">
                <iframe
                  src={spotifyEmbed}
                  width="100%"
                  height="152"
                  frameBorder="0"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  title="Spotify"
                  style={{ borderRadius: 12 }}
                />
                <button className="flocus-spotify-clear" onClick={clearSpotify}>Remove</button>
              </div>
            ) : (
              <div className="flocus-spotify-input">
                <input
                  type="text"
                  placeholder="Paste Spotify playlist or track URL..."
                  value={spotifyInput}
                  onChange={e => setSpotifyInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && saveSpotify()}
                />
                <button onClick={saveSpotify} disabled={!spotifyInput.trim()}>Connect</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── SCENES PANEL ── */}
      {showScenes && (
        <div className="flocus-panel flocus-panel-left flocus-panel-scenes">
          <div className="flocus-panel-header">
            <span>Scenes</span>
            <button className="flocus-panel-close" onClick={() => setShowScenes(false)}><X size={16} /></button>
          </div>
          <div className="flocus-panel-body">
            <p className="flocus-section-label">Gradients</p>
            <div className="flocus-scenes-grid">
              {SCENES.filter(s => s.type === 'gradient').map(s => (
                <button
                  key={s.id}
                  className={`flocus-scene-card ${scene === s.id ? 'active' : ''}`}
                  style={{ background: s.gradient, backgroundSize: '400% 400%' }}
                  onClick={() => changeScene(s.id)}
                >
                  <span>{s.label}</span>
                </button>
              ))}
            </div>
            <p className="flocus-section-label" style={{ marginTop: 16 }}>Animated</p>
            <div className="flocus-scenes-grid">
              {SCENES.filter(s => s.type === 'animated').map(s => (
                <button
                  key={s.id}
                  className={`flocus-scene-card flocus-scene-animated ${s.css} ${scene === s.id ? 'active' : ''}`}
                  onClick={() => changeScene(s.id)}
                >
                  <span>{s.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── SETTINGS PANEL ── */}
      {showSettings && (
        <div className="flocus-panel flocus-panel-right">
          <div className="flocus-panel-header">
            <span>Settings</span>
            <button className="flocus-panel-close" onClick={() => setShowSettings(false)}><X size={16} /></button>
          </div>
          <div className="flocus-panel-body">
            <label className="flocus-setting">
              <span>Focus (min)</span>
              <input type="number" value={workDuration} onChange={e => setWorkDuration(Math.max(1, +e.target.value))} />
            </label>
            <label className="flocus-setting">
              <span>Short Break (min)</span>
              <input type="number" value={breakDuration} onChange={e => setBreakDuration(Math.max(1, +e.target.value))} />
            </label>
            <label className="flocus-setting">
              <span>Long Break (min)</span>
              <input type="number" value={longBreakDuration} onChange={e => setLongBreakDuration(Math.max(1, +e.target.value))} />
            </label>
          </div>
        </div>
      )}

      {/* Toast */}
      <span className="flocus-toast" style={{ opacity: toast.visible ? 1 : 0, transform: toast.visible ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.9)' }}>
        +{toast.pts} pts ✓
      </span>
    </div>
  );
}
